// Client-side SVG export via roughjs' svg mode.
import rough from 'roughjs';
import type { CanvasElement } from '@extapia/shared';
import { boundsOf, mergeBounds } from '@extapia/shared';

export const exportToSVG = (elements: CanvasElement[], background: string, padding = 24): void => {
  const alive = elements.filter((e) => !e.isDeleted);
  if (alive.length === 0) return;
  const merged = mergeBounds(alive.map(boundsOf));
  if (!merged) return;

  const w = merged.x2 - merged.x1 + padding * 2;
  const h = merged.y2 - merged.y1 + padding * 2;
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('xmlns', svgNS);
  svg.setAttribute('width', String(w));
  svg.setAttribute('height', String(h));
  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);

  const bg = document.createElementNS(svgNS, 'rect');
  bg.setAttribute('width', '100%');
  bg.setAttribute('height', '100%');
  bg.setAttribute('fill', background);
  svg.appendChild(bg);

  const g = document.createElementNS(svgNS, 'g');
  g.setAttribute('transform', `translate(${padding - merged.x1}, ${padding - merged.y1})`);
  svg.appendChild(g);

  const rc = rough.svg(svg);
  for (const el of alive.sort((a, b) => a.index.localeCompare(b.index))) {
    const opts: {
      seed: number;
      roughness: number;
      strokeWidth: number;
      stroke: string;
      fillStyle: string;
      fill?: string;
    } = {
      seed: el.seed,
      roughness: el.roughness,
      strokeWidth: el.strokeWidth,
      stroke: el.strokeColor,
      fillStyle: el.fillStyle,
    };
    if (el.fillColor && el.fillColor !== 'transparent') opts.fill = el.fillColor;
    let node: SVGGElement | null = null;
    if (el.type === 'rect' || el.type === 'sticky' || el.type === 'frame' || el.type === 'image') {
      node = rc.rectangle(el.x, el.y, el.width, el.height, opts);
    } else if (el.type === 'ellipse') {
      node = rc.ellipse(el.x + el.width / 2, el.y + el.height / 2, el.width, el.height, opts);
    } else if (el.type === 'diamond') {
      const midX = el.x + el.width / 2;
      const midY = el.y + el.height / 2;
      node = rc.polygon(
        [
          [midX, el.y],
          [el.x + el.width, midY],
          [midX, el.y + el.height],
          [el.x, midY],
        ],
        opts,
      );
    } else if ((el.type === 'line' || el.type === 'arrow') && el.points.length >= 2) {
      const gEl = document.createElementNS(svgNS, 'g');
      for (let i = 0; i < el.points.length - 1; i++) {
        const a = el.points[i]!;
        const b = el.points[i + 1]!;
        const line = rc.line(el.x + a[0], el.y + a[1], el.x + b[0], el.y + b[1], opts);
        gEl.appendChild(line);
      }
      node = gEl;
    } else if (el.type === 'text') {
      const t = document.createElementNS(svgNS, 'text');
      t.setAttribute('x', String(el.x));
      t.setAttribute('y', String(el.y + el.fontSize));
      t.setAttribute('font-family', el.fontFamily === 4 ? 'monospace' : 'sans-serif');
      t.setAttribute('font-size', String(el.fontSize));
      t.setAttribute('fill', el.strokeColor);
      t.textContent = el.text;
      g.appendChild(t);
      continue;
    }
    if (node) g.appendChild(node);
  }

  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svg);
  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `excalidraw-extapia-${Date.now()}.svg`;
  a.click();
  URL.revokeObjectURL(url);
};
